import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Search, X, SlidersHorizontal, LayoutGrid, Rows3, FolderPlus, CheckSquare } from 'lucide-react';
import api from '@/lib/api';
import ProductCard from '@/components/ProductCard';
import BulkAddToCatalogDialog from '@/components/BulkAddToCatalogDialog';
import { useAuth } from '@/lib/AuthContext';
import { IDS } from '@/constants/testIds';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetClose,
} from '@/components/ui/sheet';

const PAGE_SIZE = 60;
const ANY = '__any__'; // Radix Select disallows empty-string values; use a sentinel for "All".

const SORT_OPTIONS = [
  { value: 'ranking', label: 'Ranking (default)' },
  { value: 'name', label: 'Name A → Z' },
  { value: 'price_asc', label: 'Price: low → high' },
  { value: 'price_desc', label: 'Price: high → low' },
  { value: 'newest', label: 'Newest synced' },
];

// -----------------------------------------------------------------------------
// Reusable filter select — used both inline on desktop and inside the mobile sheet.
// -----------------------------------------------------------------------------
function FilterSelect({ label, value, onChange, options, placeholder, testId }) {
  if (!options || options.length === 0) return null;
  return (
    <div className="min-w-0">
      <p className="eyebrow mb-2">{label}</p>
      <Select
        value={value || ANY}
        onValueChange={(v) => onChange(v === ANY ? '' : v)}
      >
        <SelectTrigger
          data-testid={testId}
          className="h-11 w-full rounded-none border-neutral-200 bg-white text-sm text-neutral-800 focus:ring-0 focus:ring-offset-0 focus:border-black data-[placeholder]:text-neutral-400"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={ANY}>All {label.toLowerCase()}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Active filter chips (removable). Shown when >=1 filter is applied.
// -----------------------------------------------------------------------------
function ActiveChips({ chips, onClearAll }) {
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="catalog-active-chips">
      {chips.map((c) => (
        <button
          key={`${c.key}:${c.value}`}
          type="button"
          onClick={c.onRemove}
          className="inline-flex items-center gap-1.5 px-3 py-1 border rounded-full sans text-xs bg-[color:var(--ije-pink-tint)] border-[color:var(--ije-pink)] text-[color:var(--ije-pink-dark)] hover:bg-white transition-colors"
          data-testid={`chip-${c.key}`}
        >
          <span className="uppercase tracking-widest text-[10px] opacity-70">{c.label}:</span>
          <span>{c.value}</span>
          <X size={12} strokeWidth={2} />
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="sans text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1 ml-1 transition-colors"
        data-testid={IDS.catalog.clearFilters}
      >
        <X size={12} /> Clear all
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// FilterPanel — the actual filter body (used inline on desktop, inside Sheet on mobile).
// -----------------------------------------------------------------------------
function FilterPanel({ data, values, setParam, layout }) {
  const { brand, category, inStock, sort } = values;
  const gridClass =
    layout === 'row'
      ? 'grid grid-cols-2 md:grid-cols-3 gap-3'
      : 'grid grid-cols-1 gap-5';
  return (
    <>
      <div className={gridClass}>
        <FilterSelect
          label="Brand"
          value={brand}
          onChange={(v) => setParam('brand', v)}
          options={data.brands}
          placeholder="All brands"
          testId={IDS.catalog.brandFilter}
        />
        <FilterSelect
          label="Product Type"
          value={category}
          onChange={(v) => setParam('category', v)}
          options={data.categories}
          placeholder="All types"
          testId={IDS.catalog.categoryFilter}
        />
        <div className="min-w-0">
          <p className="eyebrow mb-2">Sort by</p>
          <Select
            value={sort || 'ranking'}
            onValueChange={(v) => setParam('sort', v === 'ranking' ? '' : v)}
          >
            <SelectTrigger
              data-testid="catalog-sort"
              className="h-11 w-full rounded-none border-neutral-200 bg-white text-sm text-neutral-800 focus:ring-0 focus:ring-offset-0 focus:border-black"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <label className="mt-5 flex items-center gap-3 cursor-pointer w-max">
        <input
          type="checkbox"
          data-testid={IDS.catalog.inStockToggle}
          checked={inStock}
          onChange={(e) => setParam('in_stock', e.target.checked)}
          className="accent-[color:var(--ije-pink)] w-4 h-4"
        />
        <span className="sans text-sm text-neutral-700">In-stock only</span>
      </label>
    </>
  );
}

// -----------------------------------------------------------------------------
// Main Catalog page
// -----------------------------------------------------------------------------
export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], brands: [], categories: [], collections: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get('q') || '');
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const canBulk = !!user && (user.role === 'admin' || user.status === 'active');
  const toggleSku = useCallback((sku) => {
    setSelectedSkus((cur) => {
      const next = new Set(cur);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  }, []);
  const exitSelectMode = () => { setSelectMode(false); setSelectedSkus(new Set()); };
  const selectAllOnPage = () => {
    setSelectedSkus(new Set(data.items.map((p) => p.sku)));
  };
  const startSelectWith = useCallback((sku) => {
    setSelectMode(true);
    setSelectedSkus(new Set([sku]));
  }, []);
  const handleBulkAdded = (result) => {
    exitSelectMode();
    // Admins jump straight into the destination catalog's items tab so they
    // can drag-reorder what they just added.
    if (result?.catalog_id && user?.role === 'admin') {
      navigate(`/admin?tab=catalogs&edit=${result.catalog_id}&edit_tab=items`);
    }
  };
  // 'comfort' = 1 col big cards, 'compact' = 2 col dense cards. Mobile only; persists.
  const [mobileDensity, setMobileDensity] = useState(() => {
    try { return localStorage.getItem('ije_catalog_density') || 'comfort'; }
    catch { return 'comfort'; }
  });
  const toggleDensity = () => {
    const next = mobileDensity === 'comfort' ? 'compact' : 'comfort';
    setMobileDensity(next);
    try { localStorage.setItem('ije_catalog_density', next); } catch { /* localStorage unavailable */ }
  };

  const brand = params.get('brand') || '';
  const category = params.get('category') || '';
  const inStock = params.get('in_stock') === '1';
  const q = params.get('q') || '';
  const sort = params.get('sort') || 'ranking';

  const load = useCallback(async () => {
    setLoading(true);
    const qs = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE, sort };
    if (brand) qs.brand = brand;
    if (category) qs.category = category;
    if (inStock) qs.in_stock = true;
    if (q) qs.q = q;
    try {
      const res = await api.get('/products', { params: qs });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [brand, category, inStock, q, sort, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [brand, category, inStock, q, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v == null || v === '' || v === false) next.delete(k);
    else next.set(k, v === true ? '1' : v);
    setParams(next);
  };
  const clear = () => setParams({});
  const submitSearch = (e) => { e.preventDefault(); setParam('q', searchInput.trim()); };

  const activeCount = [brand, category, inStock ? 'x' : '', q]
    .filter(Boolean).length;
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  const chips = useMemo(() => {
    const list = [];
    if (q) list.push({ key: 'q', label: 'Search', value: q, onRemove: () => { setSearchInput(''); setParam('q', ''); } });
    if (brand) list.push({ key: 'brand', label: 'Brand', value: brand, onRemove: () => setParam('brand', '') });
    if (category) list.push({ key: 'category', label: 'Type', value: category, onRemove: () => setParam('category', '') });
    if (inStock) list.push({ key: 'in_stock', label: 'Filter', value: 'In stock', onRemove: () => setParam('in_stock', false) });
    return list;
  }, [q, brand, category, inStock]);

  const filterValues = { brand, category, inStock, sort };

  return (
    <div className="container-x py-14" data-testid={IDS.catalog.root}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <span className="eyebrow">The Catalog</span>
          <h1 className="h-display text-3xl md:text-5xl mt-3">Browse the collection.</h1>
        </div>
        <p className="sans text-xs uppercase tracking-[0.25em] text-neutral-500 md:mb-1">
          {loading ? 'Loading…' : `${data.total} products`}
        </p>
      </div>

      {!user && (
        <div className="mb-6 border border-neutral-200 bg-yellow-50 p-5">
          <p className="sans text-sm text-yellow-900">
            <strong>Sign in required.</strong> The catalog is trade-only. Please <Link to="/login" className="link-pink">sign in</Link> or <Link to="/apply" className="link-pink">apply for an account</Link>.
          </p>
        </div>
      )}
      {user && user.status === 'pending' && (
        <div className="mb-6 border border-neutral-200 bg-yellow-50 p-5">
          <p className="sans text-sm text-yellow-900">
            <strong>Your application is under review.</strong> Pricing will unlock as soon as an IJE HAUS rep approves your account.
          </p>
        </div>
      )}

      {/* ---------- Toolbar (search + filters trigger + sort) ---------- */}
      <div className="flex items-center gap-3 mb-4">
        <form onSubmit={submitSearch} className="relative flex-1">
          <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
          <input
            data-testid={IDS.catalog.search}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, SKU or UPC"
            className="ije-input pl-10 h-11 w-full"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setParam('q', ''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-black"
              data-testid="catalog-search-clear"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </form>

        {/* Mobile: filters trigger + density toggle */}
        <button
          type="button"
          onClick={toggleDensity}
          className="md:hidden inline-flex items-center justify-center h-11 w-11 border border-neutral-200 bg-white hover:border-black transition-colors"
          data-testid="catalog-density-toggle"
          aria-label={mobileDensity === 'comfort' ? 'Switch to compact view' : 'Switch to comfort view'}
          title={mobileDensity === 'comfort' ? 'Compact view' : 'Comfort view'}
        >
          {mobileDensity === 'comfort'
            ? <LayoutGrid size={16} strokeWidth={1.5} />
            : <Rows3 size={16} strokeWidth={1.5} />}
        </button>

        <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="md:hidden relative inline-flex items-center gap-2 h-11 px-4 border border-neutral-200 bg-white sans text-xs tracking-widest uppercase hover:border-black transition-colors"
              data-testid="catalog-open-filters"
            >
              <SlidersHorizontal size={14} />
              Filters
              {activeCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                  style={{ background: 'var(--ije-pink-dark, #C2478A)' }}
                  data-testid="catalog-filter-count"
                >
                  {activeCount}
                </span>
              )}
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85vw] sm:max-w-sm overflow-y-auto">
            <SheetHeader className="text-left">
              <SheetTitle className="h-display text-2xl">Filters</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <FilterPanel data={data} values={filterValues} setParam={setParam} layout="stack" />
            </div>
            <SheetFooter className="mt-8 flex-row gap-3">
              <button
                type="button"
                onClick={clear}
                className="flex-1 h-11 border border-neutral-200 sans text-xs tracking-widest uppercase hover:border-black"
                data-testid="catalog-sheet-clear"
              >
                Clear
              </button>
              <SheetClose asChild>
                <button
                  type="button"
                  className="flex-1 btn-primary h-11"
                  data-testid="catalog-sheet-apply"
                >
                  Show {data.total} products
                </button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {/* ---------- Desktop inline filters ---------- */}
      <div className="hidden md:block border-y border-neutral-200 py-5 mb-6">
        <FilterPanel data={data} values={filterValues} setParam={setParam} layout="row" />
      </div>

      <ActiveChips chips={chips} onClearAll={clear} />

      {/* ---------- Grid ---------- */}
      {!loading && data.items.length === 0 && (
        <div className="border border-dashed border-neutral-200 p-16 text-center" data-testid={IDS.catalog.emptyState}>
          <p className="serif text-2xl italic">No products match your filters.</p>
          <button type="button" onClick={clear} className="btn-outline mt-6">Clear filters</button>
        </div>
      )}
      <div
        className={`grid ${mobileDensity === 'compact' ? 'grid-cols-2' : 'grid-cols-1'} sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${mobileDensity === 'compact' ? 'gap-3' : 'gap-4'} md:gap-6`}
        data-testid={IDS.catalog.grid}
        data-density={mobileDensity}
      >
        {data.items.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            compact={mobileDensity === 'compact'}
            selectMode={selectMode}
            selected={selectedSkus.has(p.sku)}
            onToggle={toggleSku}
            onLongPress={canBulk ? startSelectWith : undefined}
          />
        ))}
      </div>

      {canBulk && !selectMode && (
        <button
          type="button"
          onClick={() => setSelectMode(true)}
          className="fixed bottom-6 right-6 z-40 bg-white border border-neutral-200 hover:border-black shadow-lg h-11 px-4 sans text-xs tracking-widest uppercase flex items-center gap-2"
          data-testid="catalog-select-mode-enter"
        >
          <CheckSquare size={14} /> Select mode
        </button>
      )}
      {selectMode && (
        <div
          className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-lg"
          data-testid="catalog-selection-bar"
        >
          {/* Top row: count + select all + cancel */}
          <div className="flex items-center gap-2 md:contents">
            <span className="sans text-xs tracking-widest uppercase text-neutral-500 whitespace-nowrap">
              <strong className="text-neutral-900">{selectedSkus.size}</strong> selected
            </span>
            <button
              type="button"
              onClick={selectAllOnPage}
              className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-2 whitespace-nowrap"
              data-testid="catalog-select-all"
            >
              Select all {data.items.length}
            </button>
            <div className="flex-1 md:contents" />
            <button
              type="button"
              onClick={exitSelectMode}
              className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-2"
              data-testid="catalog-select-mode-exit"
            >
              Cancel
            </button>
            {/* Desktop-only inline primary (stays in the same row) */}
            <button
              type="button"
              disabled={selectedSkus.size === 0}
              onClick={() => setBulkOpen(true)}
              className="btn-primary h-10 !hidden md:!inline-flex"
              data-testid="catalog-bulk-add"
            >
              <FolderPlus size={14} /> Add {selectedSkus.size} to catalog
            </button>
          </div>
          {/* Mobile-only full-width primary on its own row */}
          <button
            type="button"
            disabled={selectedSkus.size === 0}
            onClick={() => setBulkOpen(true)}
            className="btn-primary h-11 w-full mt-3 md:!hidden"
            data-testid="catalog-bulk-add-mobile"
          >
            <FolderPlus size={14} /> Add {selectedSkus.size} to catalog
          </button>
        </div>
      )}

      <BulkAddToCatalogDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        skus={Array.from(selectedSkus)}
        onAdded={handleBulkAdded}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-12">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-outline" data-testid="catalog-prev">Prev</button>
          <span className="sans text-xs uppercase tracking-widest text-neutral-500 px-4">Page {page} of {totalPages}</span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="btn-outline" data-testid="catalog-next">Next</button>
        </div>
      )}
    </div>
  );
}
